import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { analytics as analyticsApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ArrowLeft, BarChart3, DollarSign, CalendarDays, TrendingUp,
  Sparkles, Send, CreditCard, Hotel, Users, AlertTriangle
} from 'lucide-react';

function KPICard({ title, value, sub, icon: Icon }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold heading-font mono mt-1">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
          </div>
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AnalyticsPage() {
  const { hotelId } = useParams();
  const [data, setData] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    analyticsApi.get(hotelId).then(r => setData(r.data)).catch(console.error);
  }, [hotelId]);

  const askQuestion = async () => {
    if (!question.trim()) return;
    const q = question.trim();
    setChatMessages(prev => [...prev, { role: 'user', text: q }]);
    setQuestion('');
    setAsking(true);
    try {
      const { data: res } = await analyticsApi.chat(hotelId, { question: q });
      setChatMessages(prev => [...prev, { role: 'ai', text: res.answer }]);
    } catch (e) {
      setChatMessages(prev => [...prev, { role: 'ai', text: 'Sorry, I couldn\'t process that question.' }]);
    }
    setAsking(false);
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center"><p className="text-muted-foreground">Loading analytics...</p></div>;

  return (
    <div className="min-h-screen bg-background" data-testid="analytics-page">
      <nav className="border-b border-border bg-card/60 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/dashboard" className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <BarChart3 className="w-3.5 h-3.5 text-primary-foreground" />
            </div>
            <span className="font-bold heading-font">AI Analytics</span>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <KPICard title="Total Revenue" value={`$${data.total_revenue.toLocaleString()}`} icon={DollarSign} />
          <KPICard title="Total Bookings" value={data.total_bookings} icon={CalendarDays} />
          <KPICard title="Crypto Payments" value={`${data.crypto_pct}%`} sub={`${data.crypto_bookings} bookings`} icon={CreditCard} />
          <KPICard title="Cancellation Rate" value={`${data.cancellation_rate}%`} sub={`${data.cancelled} cancelled`} icon={AlertTriangle} />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Top Rooms */}
          <Card>
            <CardHeader><CardTitle className="heading-font text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Top Performing Rooms</CardTitle></CardHeader>
            <CardContent>
              {data.top_rooms.length === 0 ? (
                <p className="text-muted-foreground text-sm">No booking data yet.</p>
              ) : (
                <div className="space-y-4">
                  {data.top_rooms.map((room, i) => (
                    <div key={room.id}>
                      <div className="flex justify-between items-center mb-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs w-5 h-5 flex items-center justify-center p-0">#{i + 1}</Badge>
                          <span className="text-sm font-medium">{room.name}</span>
                        </div>
                        <span className="text-sm mono font-semibold">${room.revenue}</span>
                      </div>
                      <Progress value={data.top_rooms[0]?.revenue ? (room.revenue / data.top_rooms[0].revenue * 100) : 0} className="h-2" />
                      <p className="text-xs text-muted-foreground mt-1">{room.bookings} booking{room.bookings !== 1 ? 's' : ''}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Chat */}
          <Card className="flex flex-col">
            <CardHeader>
              <CardTitle className="heading-font text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Ask AI About Your Performance
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <ScrollArea className="flex-1 max-h-[300px] mb-4">
                <div className="space-y-3">
                  {chatMessages.length === 0 && (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">Try asking:</p>
                      {['How can I increase bookings?', 'What is my best performing room?', 'How are crypto payments doing?'].map((q, i) => (
                        <Button key={i} variant="outline" size="sm" className="text-xs w-full justify-start" onClick={() => { setQuestion(q); }}>
                          {q}
                        </Button>
                      ))}
                    </div>
                  )}
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[90%] rounded-xl px-3 py-2 text-sm ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                        {msg.text}
                      </div>
                    </div>
                  ))}
                  {asking && (
                    <div className="flex justify-start">
                      <div className="bg-muted rounded-xl px-3 py-2 text-sm">Analyzing your data...</div>
                    </div>
                  )}
                </div>
              </ScrollArea>
              <div className="flex gap-2">
                <Input value={question} onChange={e => setQuestion(e.target.value)}
                       onKeyDown={e => e.key === 'Enter' && askQuestion()}
                       placeholder="Ask about your hotel performance..." className="flex-1"
                       data-testid="analytics-question-input" />
                <Button onClick={askQuestion} disabled={asking} size="icon" data-testid="analytics-ask-button">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Metrics */}
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-2">Payment Methods</p>
              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-sm">Crypto</span><span className="mono text-sm">{data.crypto_bookings}</span></div>
                <Progress value={data.crypto_pct} className="h-2" />
                <div className="flex justify-between"><span className="text-sm">Pay at Property</span><span className="mono text-sm">{data.property_bookings}</span></div>
                <Progress value={100 - data.crypto_pct} className="h-2" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-2">Booking Status</p>
              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-sm">Confirmed</span><span className="mono text-sm text-emerald-600">{data.confirmed}</span></div>
                <div className="flex justify-between"><span className="text-sm">Cancelled</span><span className="mono text-sm text-red-500">{data.cancelled}</span></div>
                <div className="flex justify-between"><span className="text-sm">Pending</span><span className="mono text-sm text-amber-500">{data.pending_confirmations}</span></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-2">Performance</p>
              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-sm">Avg. Revenue/Booking</span><span className="mono text-sm">${data.total_bookings ? (data.total_revenue / data.total_bookings).toFixed(0) : 0}</span></div>
                <div className="flex justify-between"><span className="text-sm">Conversion Quality</span><span className="mono text-sm">{(100 - data.cancellation_rate).toFixed(0)}%</span></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
